/**
 * machineScheduleRowGrouping.ts
 * Pure row-model builder for the Machine Schedule grid's left column.
 * Extracted from MachineScheduleGrid.tsx (600-line cap, CODING-STANDARDS).
 *
 * TWO-LEVEL taxonomy (migration 120, lab sketch 2026-07-30):
 *   TOP category  — Machine.LabSection: Soil → Rock → Index (fixed order),
 *                   anything else alphabetically after.
 *   TEST FAMILY   — Machine.MachineGroup (admin-editable, e.g. "Triaxial",
 *                   "DSS / CDSS / DRS", "Oedometer / Consolidation"); machines
 *                   with no group yet fall back to the old name-derived type
 *                   (FEAT-2026-0014) so nothing lands in a bare bucket.
 * Both levels collapse independently in the grid.
 *
 * Also synthesizes a row for any booking whose machine is missing from the
 * active list — a booking on a DEACTIVATED machine must stay visible (G2-10:
 * those rows carry `inactiveStatus` so the grid renders a flag chip).
 */

import type { MachineBookingCalendar, LabSection } from '../../../types/schedule';

/** Fixed top-category order per the lab's sketch; unknowns follow alphabetically. */
const TOP_ORDER = ['Soil', 'Rock', 'Index'];

/**
 * FEAT-2026-0014 — derive a machine "type" from its name, used ONLY as the
 * fallback group for machines whose MachineGroup is not set yet. Strip a
 * trailing numeric index and an optional trailing single-letter index:
 *   "Atterberg 1" → "Atterberg", "EMC01" → "EMC", "Triaxial Cell A" → "Triaxial Cell".
 */
export function deriveMachineType(name: string | null | undefined, code: string): string {
  const raw = (name ?? '').trim();
  const stripped = raw
    .replace(/[\s-]*\d+\s*$/, '')   // trailing number: "Atterberg 1" / "EMC01" → base
    .replace(/\s+[A-Za-z]$/, '')    // trailing single-letter index: "…Cell A" → "…Cell"
    .trim();
  return stripped || raw || code || 'Other';
}

/** One left-column machine row of the Machine Schedule grid. */
export interface MachineGridRow {
  machineKey: string;
  code: string;
  name: string;
  /**
   * G2-10: set ONLY on synthesized rows (machine absent from the active list)
   * — the deactivated machine's Status ('Decommissioned' / 'OutOfService' /…),
   * rendered as a flag chip. Undefined for normal active rows.
   */
  inactiveStatus?: string;
}

/** The input row shape (from useScheduleData / listCapableMachines). */
export interface MachineRowInput {
  machineKey: string;
  code: string;
  name: string;
  labSection: LabSection | null;
  machineGroup?: string | null;
}

/** One second-level (test family) group. */
export interface MachineGroupSection {
  /** e.g. "Triaxial" — MachineGroup, else the name-derived fallback. */
  name: string;
  /** Stable collapse key: `${top}/${name}`. */
  key: string;
  machines: MachineGridRow[];
}

/** One top category (Soil / Rock / Index / …). */
export interface MachineTopSection {
  /** e.g. "Soil". */
  top: string;
  /** Stable collapse key: `top:${top}`. */
  key: string;
  groups: MachineGroupSection[];
  machineCount: number;
}

const topOf = (labSection: LabSection | null | undefined): string => labSection ?? 'Other';

/**
 * Gigi/Mike 2026-07-31 ("we only do the index… it is by test — we didn't
 * assign the machine"): the schedule view hides the physical INDEX stations —
 * index tests are pooled per TEST, never booked per machine, so station lanes
 * are noise. A station that carries an ACTUAL booking in the window (e.g. a
 * manually-added maintenance event) stays visible; the virtual INDEX pool
 * machine is always dropped (its single lane is superseded by the per-test
 * rows the grid renders instead).
 */
export function filterIndexStationsForSchedule(
  rows: ReadonlyArray<MachineRowInput>,
  bookings: readonly MachineBookingCalendar[],
): MachineRowInput[] {
  const bookedKeys = new Set(bookings.map((b) => b.MachineKey));
  return rows.filter((m) => {
    if (m.labSection !== 'Index') return true;
    if (m.code.toUpperCase() === 'INDEX') return false;
    return bookedKeys.has(m.machineKey);
  });
}

/** Collapse key for a top category. */
export function topSectionKey(top: string): string {
  return `top:${top}`;
}

/** Collapse key for a test-family group under a top category. */
export function groupSectionKey(top: string, group: string): string {
  return `${top}/${group}`;
}

/**
 * Build the two-level model: top categories (Soil → Rock → Index) → test-family
 * groups → machine rows. Active machines first; then synthesized rows for
 * bookings on machines missing from the active list (grouped by the booking's
 * own LabSection when it carries one, else 'Other', with the name-derived
 * family). Groups and rows sort alphabetically inside their level.
 */
export function groupMachineRowsTwoLevel(
  allMachineRows: ReadonlyArray<MachineRowInput>,
  bookings: readonly MachineBookingCalendar[],
): MachineTopSection[] {
  // top -> group -> rows
  const tops = new Map<string, Map<string, MachineGridRow[]>>();

  const push = (top: string, group: string, row: MachineGridRow) => {
    let groups = tops.get(top);
    if (!groups) { groups = new Map(); tops.set(top, groups); }
    let rows = groups.get(group);
    if (!rows) { rows = []; groups.set(group, rows); }
    if (!rows.find((r) => r.machineKey === row.machineKey)) rows.push(row);
  };

  for (const m of allMachineRows) {
    const group = (m.machineGroup ?? '').trim() || deriveMachineType(m.name, m.code);
    push(topOf(m.labSection), group, { machineKey: m.machineKey, code: m.code, name: m.name });
  }

  for (const b of bookings) {
    if (allMachineRows.find((m) => m.machineKey === b.MachineKey)) continue;
    const top = topOf(b.MachineLabSection ?? null);
    const group = deriveMachineType(b.MachineName, b.MachineCode);
    push(top, group, {
      machineKey: b.MachineKey,
      code: b.MachineCode,
      name: b.MachineName,
      inactiveStatus: b.MachineStatus || 'Decommissioned',
    });
  }

  const topRank = (t: string): number => {
    const i = TOP_ORDER.indexOf(t);
    return i === -1 ? TOP_ORDER.length : i;
  };

  return [...tops.entries()]
    .sort(([a], [b]) => topRank(a) - topRank(b) || a.localeCompare(b))
    .map(([top, groups]) => {
      const groupSections: MachineGroupSection[] = [...groups.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([name, machines]) => ({
          name,
          key: groupSectionKey(top, name),
          machines: machines.sort((a, b) => a.code.localeCompare(b.code)),
        }));
      return {
        top,
        key: topSectionKey(top),
        groups: groupSections,
        machineCount: groupSections.reduce((s, g) => s + g.machines.length, 0),
      };
    });
}
